import {connect} from 'react-redux';
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import { ResponsiveContainer, ComposedChart, Bar, Line, Cell, XAxis, YAxis, ReferenceLine, Legend, LabelList } from 'recharts';
import ReferenceLabel from './reference-label';
import DownloadLink from './download-link';
import './styles.css';

class IrelandChart extends Component {
  constructor(props) {
    super(props);

    this.tooltip = null;
    this.state = {
      xAxisDy: 0
    };
  }

  componentDidMount() {
    this.props.barValueKeys.map(({labelPosition}) =>
      labelPosition === 'bottom' && this.setState({ xAxisDy: 20 }));
  }

  get refLines() {
    return this.props.refLines.map(line => (
      <ReferenceLine
        key={line.label}
        y={line.value}
        yAxisId='left'
        label={<ReferenceLabel value={line.label}/>}
        stroke={this.props.refLineColor}
        strokeDasharray={this.props.refLinePattern}
      />
    ));
  }

  onBarMouseEnter = key => (e) => {
    if (this.props.showTooltip) {
      this.updateTooltip(e, key);
    }
  };

  onBarMouseLeave = () => {
    if (this.props.showTooltip) {
      this.updateTooltip(null);
    }
  };

  updateTooltip(props, key) {
    if (props !== null) {
      let textCount = 0;
      const tooltip = props.tooltips[key];

      if (tooltip.includes('\n')) {
        const points = tooltip.split('\n')[0];
        const percent = tooltip.split('\n')[1];
        const pointsLength = points.length;
        const percentLength = percent.length;

        textCount = pointsLength > percentLength ? pointsLength : percentLength * (4 / 3);

        this.tooltip.childNodes[0].style.display = 'block';
        this.tooltip.childNodes[0].innerHTML = points;
        this.tooltip.childNodes[1].innerHTML = percent;
      } else {
        textCount = tooltip.length;

        this.tooltip.childNodes[0].style.display = 'none';
        this.tooltip.childNodes[1].innerHTML = tooltip;
      }

      const width = textCount * 10;
      this.tooltip.style.width = `${width}px`;
      const x = Math.round(props.x) - ((width - props.width) / 2);
      const y = Math.round(props.y) - 60;
      this.tooltip.style.opacity = '1';
      this.tooltip.style.transform = `translate(${x}px, ${y}px)`;
    } else {
      this.tooltip.style.opacity = '0';
    }
  }

  get bars() {
    return this.props.barValueKeys.map(({
      key, axisId, label, labelPosition, stackId, color, name
    }) => (
      < Bar
        dataKey={key}
        key={key}
        yAxisId={axisId || 'left'}
        stackId={stackId}
        fill={color || '#ffc734'}
        name={name || key}
        isAnimationActive={false}
        onMouseEnter={this.onBarMouseEnter(key)}
        onMouseLeave={this.onBarMouseLeave}
      >
        {
          this.props.data.map((entry, index) => (
            <Cell
              cursor='pointer'
              className={`bar ${key}-bar`}
              key={`cell-${index}`}
            />
          ))
        }
        {label && <LabelList dataKey={label} position={labelPosition} />}
      </Bar>
    ));
  }

  get lines() {
    return this.props.lineValueKeys.map(({
      key, axisId, lineType, color, name, dot
    }) => (
      <Line
        type={lineType || 'linear'}
        key={key}
        dataKey={key}
        class={`line ${key}-line`}
        yAxisId={axisId}
        stroke={color}
        name={name}
        dot={dot || false}
        isAnimationActive={false}
      />
    ));
  }

  render() {
    return (
      this.props.data && (
      <div className={this.props.active ? 'ireland-chart-wrapper' : 'ireland-chart-wrapper-disabled'}>
        <div className="flex-container">
          {!this.props.active && <div className="locked" />}
          <div className='chart-title'>
            <h3>{this.props.name}</h3>
            {this.props.downloadLink && (
              <DownloadLink
                link={this.props.downloadLink}
              />
            )}
          </div>
        </div>
        <div className='ireland-chart-container'>
          {this.props.showTooltip && (
          <div className='tooltip' ref={(ref) => { this.tooltip = ref; } }>
            <div className='points' />
            <div className='percent' />
          </div>
          )}
          <ResponsiveContainer width='95%' minHeight={300}>
            <ComposedChart
              className='ireland-chart'
              data={this.props.data}
            >
              {this.bars}
              {this.lines}
              {this.refLines}
              {this.props.showLegend && (
              <Legend
                align={this.props.legendAlign}
                verticalAlign={this.props.legendVAlign}
                height={this.props.legendHeight}
                payload={this.props.legendPayload}
              />
              )}
              <XAxis
                dataKey='label'
                tick={{dy: this.state.xAxisDy, width: this.props.xTickWidth}}
                interval={0}
                label={{
                value: this.props.xLabel, position: 'insideBottom', offset: 0
              }}
              />
              <YAxis
                yAxisId='left'
                type='number'
                ticks={this.props.leftTicks}
                interval={0}
                unit={this.props.leftTickUnit}
                label={{
                value: this.props.yLeftLabel, angle: -90, position: 'insideLeft', dy: 40
              }}
              />
              {this.props.rightTicks && (
              <YAxis
                yAxisId='right'
                type='number'
                orientation='right'
                ticks={this.props.rightTicks}
                interval={0}
                unit={this.props.rightTickUnit}
                label={{
                value: this.props.yRightLabel, angle: -90, position: 'insideRight', dy: -40
              }}
              />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
      ));
  }
}

IrelandChart.propTypes = {
  xLabel: PropTypes.string,
  yLeftLabel: PropTypes.string,
  yRightLabel: PropTypes.string,
  data: PropTypes.array.isRequired,
  barValueKeys: PropTypes.array,
  lineValueKeys: PropTypes.array,
  refLines: PropTypes.array,
  refLineColor: PropTypes.string,
  refLinePattern: PropTypes.string,
  xTickWidth: PropTypes.number,
  leftTicks: PropTypes.array,
  leftTickUnit: PropTypes.string,
  rightTicks: PropTypes.array,
  rightTickUnit: PropTypes.string,
  showLegend: PropTypes.bool,
  showTooltip: PropTypes.bool,
  legendAlign: PropTypes.string,
  legendVAlign: PropTypes.string,
  legendHeight: PropTypes.number,
  legendPayload: PropTypes.array,
  downloadLink: PropTypes.string,
  active: PropTypes.bool
};

IrelandChart.defaultProps = {
  xLabel: '',
  yLeftLabel: '',
  yRightLabel: '',
  barValueKeys: [],
  lineValueKeys: [],
  xTickWidth: 0,
  leftTicks: [0, 10],
  leftTickUnit: '',
  rightTicks: [],
  rightTickUnit: 'k',
  refLines: [],
  refLineColor: '#ab1738',
  refLinePattern: '3 3',
  showLegend: false,
  showTooltip: false,
  legendAlign: 'left',
  legendVAlign: 'top',
  legendHeight: 50,
  legendPayload: null,
  active: true
};

export default connect()(IrelandChart);



// WEBPACK FOOTER //
// ./src/components/common/chart/index.jsx